// Text-to-Speech ユーティリティ（問題読み上げ用）
// アナウンサー風の自然な読み上げ
// Google Cloud TTS（高品質）またはWeb Speech API（フォールバック）

import { FL_SCRIPT_LABELS } from './fiveLeague';

// Google Cloud TTS設定
const GOOGLE_TTS_API_KEY = import.meta.env.VITE_GOOGLE_TTS_API_KEY;
const GOOGLE_TTS_ENDPOINT = 'https://texttospeech.googleapis.com/v1/text:synthesize';

// Google Cloud TTSが使えるか
const useGoogleTTS = GOOGLE_TTS_API_KEY && GOOGLE_TTS_API_KEY !== 'YOUR_API_KEY_HERE';

// 音声キャッシュ（同じテキストを何度も合成しない）
const audioCache = new Map();

let cachedVoice = null;

/**
 * 日本語音声を取得（高品質・男性声優先）
 */
function getJapaneseVoice() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  if (cachedVoice) return cachedVoice;

  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return null;

  // 日本語声を探す
  const jaVoices = voices.filter(v => v.lang.startsWith('ja'));

  if (jaVoices.length === 0) {
    cachedVoice = voices[0];
    return cachedVoice;
  }

  // 高品質な声を優先（優先度順）
  // Chrome: Hattori使用可能、Safari: Hattori使用不可
  const preferredVoices = [
    'hattori',           // Chrome - Siri男性声（ベスト）
    'google 日本語',      // Chrome標準
    'reed',              // Safari - 男性声
    'eddy',              // Safari - 男性声
  ];

  const femaleVoiceNames = ['kyoko', 'o-ren', 'oren', 'haruka', 'sayaka', 'nanami', 'mizuki'];

  // 優先リストから順に探す
  let selectedVoice = null;
  for (const preferred of preferredVoices) {
    selectedVoice = jaVoices.find(v =>
      v.name.toLowerCase().includes(preferred.toLowerCase())
    );
    if (selectedVoice) break;
  }

  // 見つからなければ、女性声を避けて選択
  if (!selectedVoice) {
    selectedVoice = jaVoices.find(v => {
      const name = v.name.toLowerCase();
      return !femaleVoiceNames.some(female => name.includes(female));
    });
  }

  // それでもなければ最初の日本語声
  cachedVoice = selectedVoice || jaVoices[0];

  // 全ての声を詳細にログ
  console.log('[TTS] ===== 利用可能な日本語の声 =====');
  jaVoices.forEach((v, i) => {
    console.log(`[TTS] ${i + 1}. "${v.name}" (${v.lang}, ${v.localService ? 'ローカル' : 'ネットワーク'})`);
  });
  console.log('[TTS] ===== 選択された声 =====');
  console.log(`[TTS] → "${cachedVoice?.name}"`);

  return cachedVoice;
}

// 初期化
if (typeof window !== 'undefined' && window.speechSynthesis) {
  // 初回読み込み
  window.speechSynthesis.getVoices();
  // 音声リスト更新時にキャッシュをクリア
  window.speechSynthesis.onvoiceschanged = () => {
    cachedVoice = null;
    getJapaneseVoice();
  };
}

// デフォルトの読み上げ速度
const DEFAULT_RATE = 0.9;  // 0.85は一部のブラウザで問題がある可能性
const DEFAULT_PITCH = 0.95;  // 少し低めでかっこよく

// 現在再生中のAudio
let currentAudio = null;

// 効果音のプリロード
const soundEffects = {};
const SOUND_URLS = {
  question: '/sounds/question.mp3',
  timeup: '/sounds/timeup.mp3',
  reveal: '/sounds/reveal.mp3',
  correct: '/sounds/correct.mp3',
  incorrect: '/sounds/incorrect.mp3',
};

/**
 * 効果音を再生
 */
export function playSoundEffect(name) {
  return new Promise((resolve) => {
    const url = SOUND_URLS[name];
    if (!url) {
      resolve();
      return;
    }

    // キャッシュがあれば使う
    if (soundEffects[name]) {
      const audio = soundEffects[name].cloneNode();
      audio.volume = 0.7;
      audio.onended = resolve;
      audio.onerror = resolve;
      audio.play().catch(() => resolve());
      return;
    }

    // 新規作成
    const audio = new Audio(url);
    audio.volume = 0.7;
    soundEffects[name] = audio;
    audio.onended = resolve;
    audio.onerror = resolve;
    audio.play().catch(() => resolve());
  });
}

/**
 * Google Cloud TTSで音声を合成して再生
 * SSMLもサポート（textが<speak>で始まる場合）
 */
async function speakWithGoogleTTS(text, options = {}) {
  const isSSML = text.trim().startsWith('<speak>');
  const cacheKey = `${text}_${options.speakingRate || 1.0}_${options.pitch || 0}`;

  // キャッシュがあれば使う
  if (audioCache.has(cacheKey)) {
    return playAudio(audioCache.get(cacheKey));
  }

  try {
    const input = isSSML ? { ssml: text } : { text };

    const response = await fetch(`${GOOGLE_TTS_ENDPOINT}?key=${GOOGLE_TTS_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        input,
        voice: {
          languageCode: 'ja-JP',
          name: 'ja-JP-Neural2-C',  // 男性のNeural2声（高品質）
        },
        audioConfig: {
          audioEncoding: 'MP3',
          speakingRate: options.speakingRate || 1.0,
          pitch: options.pitch || 0,
          volumeGainDb: options.volumeGainDb || 0,
        },
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[Google TTS] API Error:', error);
      throw new Error(error.error?.message || 'API Error');
    }

    const data = await response.json();
    const audioContent = data.audioContent;
    const audioUrl = `data:audio/mp3;base64,${audioContent}`;

    // キャッシュに保存
    audioCache.set(cacheKey, audioUrl);

    return playAudio(audioUrl);
  } catch (e) {
    console.error('[Google TTS] Error:', e);
    // フォールバック: Web Speech API
    return speakWithWebSpeech(text, options);
  }
}

/**
 * Audio要素で再生
 */
function playAudio(url) {
  return new Promise((resolve) => {
    // 前の音声を停止
    if (currentAudio) {
      currentAudio.pause();
      currentAudio = null;
    }

    const audio = new Audio(url);
    currentAudio = audio;

    audio.onended = () => {
      currentAudio = null;
      resolve();
    };
    audio.onerror = (e) => {
      console.error('[Audio] Playback error:', e);
      currentAudio = null;
      resolve();
    };

    audio.play().catch((e) => {
      console.error('[Audio] Play failed:', e);
      resolve();
    });
  });
}

/**
 * Web Speech APIで読み上げ（フォールバック用）
 */
function speakWithWebSpeech(text, options = {}) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      resolve();
      return;
    }

    const synth = window.speechSynthesis;
    if (synth.paused) synth.resume();

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getJapaneseVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = 'ja-JP';
    utterance.rate = options.rate ?? DEFAULT_RATE;
    utterance.pitch = options.pitch ?? DEFAULT_PITCH;
    utterance.volume = options.volume ?? 1.0;

    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();

    synth.speak(utterance);
  });
}

/**
 * テキストを読み上げる（キャンセルなし版）
 * 回答読み上げなど、途中でキャンセルしないもの用
 */
function speakWithoutCancel(text, options = {}) {
  // Google TTSの場合はspeakと同じ
  if (useGoogleTTS) {
    const speakingRate = options.rate ?? 1.0;
    const pitch = ((options.pitch ?? 1.0) - 1.0) * 10;
    return speakWithGoogleTTS(text, { speakingRate, pitch });
  }

  return new Promise((resolve) => {
    if (!window.speechSynthesis) {
      resolve();
      return;
    }

    const synth = window.speechSynthesis;
    if (synth.paused) synth.resume();

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getJapaneseVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = 'ja-JP';
    utterance.rate = options.rate ?? DEFAULT_RATE;
    utterance.pitch = options.pitch ?? DEFAULT_PITCH;
    utterance.volume = options.volume ?? 1.0;

    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();

    synth.speak(utterance);
  });
}

/**
 * テキストを読み上げる
 * Google Cloud TTS（利用可能な場合）またはWeb Speech API
 * @param {string} text - 読み上げるテキスト
 * @param {Object} options - オプション
 * @returns {Promise<void>}
 */
export async function speak(text, options = {}) {
  console.log('[TTS] Speaking:', text.substring(0, 20), useGoogleTTS ? '(Google)' : '(WebSpeech)');

  if (useGoogleTTS) {
    // Google Cloud TTS: rateとpitchを変換
    // Web Speech: rate 0.1-10 (default 1), pitch 0-2 (default 1)
    // Google TTS: speakingRate 0.25-4.0, pitch -20 to 20
    const speakingRate = options.rate ?? 1.0;
    const pitch = ((options.pitch ?? 1.0) - 1.0) * 10;  // 0-2 → -10 to 10
    return speakWithGoogleTTS(text, { speakingRate, pitch });
  } else {
    return speakWithWebSpeech(text, options);
  }
}

/**
 * 間を置く
 */
function pause(ms) {
  return new Promise(r => setTimeout(r, ms));
}

/**
 * 問題文をクリーンアップ（括弧内を全て除去）
 */
function cleanPromptText(prompt) {
  return prompt
    // すべての全角括弧（）内を除去
    .replace(/（[^）]*）/g, '')
    // すべての半角括弧()内を除去
    .replace(/\([^)]*\)/g, '')
    .trim();
}

/**
 * 問題の読み上げ時間を推定（ミリ秒）
 * Game.jsx でタイマーを設定する際に使用
 */
export function estimateReadingDuration(question, questionNumber) {
  if (!question?.prompt) return 5000;

  const scriptLabel = FL_SCRIPT_LABELS[question.script] || 'ひらがな';
  const cleanPrompt = cleanPromptText(question.prompt);

  // 「第N問」の読み上げ時間 + 間（500ms）
  const questionNumTime = 1000 + 500;

  // 問題文の読み上げ時間（rate=1.1で1文字約150ms）
  const fullQuestion = `${cleanPrompt}を、${scriptLabel}で、お答えください！`;
  const questionTime = fullQuestion.length * 150;

  // 読み終わりの余韻（300ms）+ バッファ（1秒）
  const endPause = 300 + 1000;

  return questionNumTime + questionTime + endPause;
}

/**
 * 問題を読み上げる（アナウンサー風・テンション高め）
 * フォーマット: "第N問。（問題文）を、（文字種）で、お答えください！"
 */
export async function readQuestion(question, questionNumber) {
  if (!question?.prompt) return;

  // 読み上げ開始前にキューをクリア（Chrome対策）
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
    await pause(100);
  }

  const scriptLabel = FL_SCRIPT_LABELS[question.script] || 'ひらがな';
  const cleanPrompt = cleanPromptText(question.prompt);

  if (useGoogleTTS) {
    // 「第N問！」を読み上げ
    const ssml1 = `<speak>
      <prosody rate="110%" pitch="+2st" volume="+1dB">第${questionNumber}問！</prosody>
    </speak>`;
    await speakWithGoogleTTS(ssml1);

    // 効果音を鳴らす
    await playSoundEffect('question');
    await pause(300);

    // 問題文を読み上げ
    const ssml2 = `<speak>
      <prosody rate="110%" pitch="+0.5st">${cleanPrompt}を、${scriptLabel}で、</prosody>
      <prosody rate="120%" pitch="+3st" volume="+2dB">お答えください！</prosody>
    </speak>`;
    await speakWithGoogleTTS(ssml2);
  } else {
    await speak(`第${questionNumber}問`, { rate: 1.0, pitch: 0.85 });
    await playSoundEffect('question');
    await pause(500);
    const fullQuestion = `${cleanPrompt}を、${scriptLabel}で、お答えください！`;
    await speak(fullQuestion, { rate: 1.1, pitch: 0.95 });
  }

  await pause(300);
}

/**
 * 回答オープンを読み上げる（テンション高め！）
 * 「回答オープン！」→ 効果音+表示同時 → 回答読み上げ → 正解/不正解
 * @param {string[]} letters - 回答の文字配列
 * @param {boolean} isPerfect - 全問正解かどうか
 * @param {Function} onRevealStart - 効果音後に呼ばれるコールバック
 * @param {string} reading - 回答の読み仮名（オプション）
 */
export async function readAnswerReveal(letters, isPerfect, onRevealStart, reading = null) {
  // 読み上げ開始前にキューをクリア（Chrome対策）
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
    await pause(100);
  }

  // 読み仮名が指定されていればそちらを使用、なければ文字を連結
  const answer = reading || letters.map(c => c?.trim() || '').join('');

  if (useGoogleTTS) {
    // 「回答オープン！」を読み上げ
    const ssml1 = `<speak>
      <prosody rate="110%" pitch="+1st">回答</prosody>
      <break time="200ms"/>
      <prosody rate="130%" pitch="+3st" volume="+2dB">オープン！</prosody>
    </speak>`;
    await speakWithGoogleTTS(ssml1);

    // 効果音と同時にアニメーション開始（awaitしない）
    playSoundEffect('reveal');
    if (onRevealStart) onRevealStart();
    await pause(400);

    // 回答を読み上げ
    if (answer) {
      const ssml2 = `<speak>
        <prosody rate="110%" pitch="+1st">${answer}</prosody>
      </speak>`;
      await speakWithGoogleTTS(ssml2);
      await pause(200);
    }

    // 正解/不正解（効果音と音声を同時再生）
    playSoundEffect(isPerfect ? 'correct' : 'incorrect');
    const resultSSML = isPerfect
      ? `<speak><prosody rate="105%" pitch="+4st" volume="+4dB">正解っ！</prosody></speak>`
      : `<speak><prosody rate="100%" pitch="-1st" volume="+2dB"><phoneme alphabet="x-jeita" ph="ザンネン">残念</phoneme></prosody></speak>`;
    await speakWithGoogleTTS(resultSSML);
  } else {
    await speak('回答、オープン！', { rate: 1.1, pitch: 0.9 });
    // 効果音と同時にアニメーション開始
    playSoundEffect('reveal');
    if (onRevealStart) onRevealStart();
    await pause(400);
    if (answer) {
      await speak(answer, { rate: 1.0, pitch: 0.95 });
    }
    await pause(200);
    // 正解/不正解（効果音と音声を同時再生）
    playSoundEffect(isPerfect ? 'correct' : 'incorrect');
    if (isPerfect) {
      await speak('正解っ！', { rate: 1.05, pitch: 1.05 });
    } else {
      await speak('残念っ', { rate: 1.0, pitch: 0.9 });
    }
  }
  await pause(200);
}

/**
 * 結果を読み上げる（互換性のため残す）
 */
export async function announceResult(isPerfect) {
  // readAnswerRevealで読み上げ済みなので何もしない
}

/**
 * 正解を読み上げる（不正解時）
 * @param {string[]} answerWord - 正解の文字配列
 * @param {string} reading - 正解の読み仮名（オプション）
 */
export async function readCorrectAnswer(answerWord, reading = null) {
  await pause(200);
  // 読み仮名が指定されていればそちらを使用、なければ文字を連結
  const answer = reading || answerWord.map(c => c?.trim() || '').join('');

  if (useGoogleTTS) {
    const ssml = `<speak>
      <prosody rate="110%" pitch="+0.5st">正解は</prosody>
      <break time="100ms"/>
      <prosody rate="115%" pitch="+1.5st" volume="+1dB">${answer}</prosody>
      <break time="100ms"/>
      <prosody rate="110%">でした</prosody>
    </speak>`;
    await speakWithGoogleTTS(ssml);
  } else {
    await speak(`正解は、${answer}、でした`, { rate: 1.2, pitch: 0.9 });
  }
  await pause(200);
}

/**
 * 次の問題へのアナウンス
 */
export async function announceNextQuestion() {
  await pause(300);
  if (useGoogleTTS) {
    const ssml = `<speak>
      <prosody rate="110%" pitch="+1st">続いて</prosody>
      <break time="150ms"/>
      <prosody rate="115%" pitch="+2st" volume="+1dB">次の問題です！</prosody>
    </speak>`;
    await speakWithGoogleTTS(ssml);
  } else {
    await speak('続いて、次の問題です', { rate: 1.1, pitch: 0.9 });
  }
  await pause(300);
}

/**
 * ゲームクリアのアナウンス
 */
export async function announceGameClear(correctCount, totalQuestions) {
  await pause(500);

  if (useGoogleTTS) {
    if (correctCount === totalQuestions) {
      const ssml = `<speak>
        <prosody rate="90%" pitch="+4st" volume="+4dB">パーフェクト！</prosody>
        <break time="600ms"/>
        <prosody rate="115%" pitch="+2st">${totalQuestions}問、全問正解です！</prosody>
        <break time="300ms"/>
        <prosody rate="120%" pitch="+3st" volume="+2dB">おめでとうございます！</prosody>
      </speak>`;
      await speakWithGoogleTTS(ssml);
    } else {
      const ssml = `<speak>
        <prosody rate="95%" pitch="-1st">ゲーム終了</prosody>
        <break time="500ms"/>
        <prosody rate="105%" pitch="+0.5st">${totalQuestions}問中、${correctCount}問正解でした</prosody>
      </speak>`;
      await speakWithGoogleTTS(ssml);
    }
  } else {
    if (correctCount === totalQuestions) {
      await speak('パーフェクト！', { rate: 1.0, pitch: 1.0 });
      await pause(400);
      await speak(`${totalQuestions}問、全問正解です！おめでとうございます！`, { rate: 1.1, pitch: 0.95 });
    } else {
      await speak('ゲーム終了', { rate: 1.0, pitch: 0.85 });
      await pause(400);
      await speak(`${totalQuestions}問中、${correctCount}問正解でした`, { rate: 1.1, pitch: 0.9 });
    }
  }
}

/**
 * 読み上げを停止
 */
export function stopSpeaking() {
  // Google TTS: Audio要素を停止
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
  // Web Speech API: 停止
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
}

/**
 * 音声合成が利用可能かチェック
 */
export function isTTSAvailable() {
  return typeof window !== 'undefined' && !!window.speechSynthesis;
}

/**
 * Google Cloud TTSの声一覧
 */
export const GOOGLE_VOICES = [
  { id: 'ja-JP-Neural2-C', label: '男性A (Neural2-C)', gender: 'male' },
  { id: 'ja-JP-Neural2-D', label: '男性B (Neural2-D)', gender: 'male' },
  { id: 'ja-JP-Neural2-B', label: '女性 (Neural2-B)', gender: 'female' },
  { id: 'ja-JP-Wavenet-C', label: '男性 (Wavenet-C)', gender: 'male' },
  { id: 'ja-JP-Wavenet-D', label: '男性 (Wavenet-D)', gender: 'male' },
];

/**
 * 指定した声でテスト再生
 */
export async function testVoice(voiceId, text = 'こんにちは') {
  if (!useGoogleTTS) {
    console.log('[TTS] Google TTS not available');
    return false;
  }

  try {
    const response = await fetch(`${GOOGLE_TTS_ENDPOINT}?key=${GOOGLE_TTS_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        input: { text },
        voice: { languageCode: 'ja-JP', name: voiceId },
        audioConfig: { audioEncoding: 'MP3', speakingRate: 1.0, pitch: 0 },
      }),
    });

    if (!response.ok) throw new Error('API Error');
    const data = await response.json();
    await playAudio(`data:audio/mp3;base64,${data.audioContent}`);
    return true;
  } catch (e) {
    console.error('[TTS] Voice test failed:', e);
    return false;
  }
}

// イントロ音声のプリ生成MP3パス
const INTRO_AUDIO_PATH = '/sounds/narration/intro-v2.mp3';

/**
 * プリ生成されたイントロ音声を再生
 */
async function playIntroAudio() {
  return new Promise((resolve) => {
    const audio = new Audio(INTRO_AUDIO_PATH);
    currentAudio = audio;

    audio.onended = () => {
      currentAudio = null;
      console.log('[TTS] Intro audio - success!');
      resolve(true);
    };
    audio.onerror = (e) => {
      console.error('[TTS] Intro audio - failed:', e);
      currentAudio = null;
      resolve(false);
    };

    audio.play().catch((e) => {
      console.error('[TTS] Intro audio play failed:', e);
      resolve(false);
    });
  });
}

/**
 * TTS をテスト（ユーザーインタラクション後に呼ぶ）
 * プリ生成されたイントロ音声を再生
 */
export async function testTTS() {
  console.log('[TTS] Testing with pre-generated intro audio...');

  // まずWeb Speech APIを初期化（バックグラウンドで）
  // これによりゲーム中のTTSが確実に動作する
  if (isTTSAvailable()) {
    window.speechSynthesis.cancel();
    window.speechSynthesis.getVoices();
  }

  // プリ生成されたイントロ音声を再生
  const success = await playIntroAudio();
  if (success) {
    return true;
  }

  // フォールバック: Web Speech APIでテスト
  console.log('[TTS] Falling back to Web Speech API test...');
  if (!isTTSAvailable()) {
    return false;
  }

  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    cachedVoice = null;
    synth.cancel();

    const utterance = new SpeechSynthesisUtterance('準備完了');
    const voice = getJapaneseVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = 'ja-JP';
    utterance.rate = 0.9;
    utterance.pitch = DEFAULT_PITCH;
    utterance.volume = 0.5;

    utterance.onend = () => {
      console.log('[TTS] Web Speech API - success!');
      resolve(true);
    };
    utterance.onerror = () => resolve(false);

    synth.speak(utterance);
  });
}
